package org.example.project.planner.components

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Icon
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import org.example.project.planner.SaveStatus
import org.example.project.ui.icons.AppIcons
import org.example.project.ui.theme.LocalPlayfulShapes
import org.example.project.ui.theme.PlayfulColors

@Composable
fun PlannerStatusBanners(
    saveStatus: SaveStatus,
    validationError: String?,
    generalError: String?,
    onRetrySave: () -> Unit,
    onReloadFromServer: () -> Unit,
    modifier: Modifier = Modifier
) {
    val shapes = LocalPlayfulShapes.current

    Column(modifier = modifier.fillMaxWidth()) {
        // Success Saved Banner
        if (saveStatus is SaveStatus.Saved) {
            Surface(
                color = PlayfulColors.SuccessContainer,
                shape = shapes.badge,
                border = BorderStroke(1.dp, PlayfulColors.SuccessBorder),
                modifier = Modifier.fillMaxWidth().padding(horizontal = 16.dp, vertical = 4.dp)
            ) {
                Row(
                    modifier = Modifier.padding(horizontal = 14.dp, vertical = 8.dp),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Icon(
                        imageVector = AppIcons.Check,
                        contentDescription = null,
                        tint = PlayfulColors.OnSuccessContainer,
                        modifier = Modifier.size(16.dp)
                    )
                    Spacer(modifier = Modifier.width(8.dp))
                    Text(
                        text = "Zapisano",
                        color = PlayfulColors.OnSuccessContainer,
                        fontWeight = FontWeight.Bold,
                        fontSize = 13.sp
                    )
                }
            }
        }

        // Saving In Progress Banner
        if (saveStatus is SaveStatus.Saving) {
            Surface(
                color = PlayfulColors.CoralLight,
                shape = shapes.badge,
                border = BorderStroke(1.dp, PlayfulColors.CoralBorder),
                modifier = Modifier.fillMaxWidth().padding(horizontal = 16.dp, vertical = 4.dp)
            ) {
                Row(
                    modifier = Modifier.padding(horizontal = 14.dp, vertical = 8.dp),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    CircularProgressIndicator(
                        modifier = Modifier.size(16.dp),
                        color = PlayfulColors.Coral,
                        strokeWidth = 2.dp
                    )
                    Spacer(modifier = Modifier.width(10.dp))
                    Text(
                        text = "Zapisywanie...",
                        fontWeight = FontWeight.SemiBold,
                        color = PlayfulColors.CoralDark,
                        fontSize = 13.sp
                    )
                }
            }
        }

        // Conflict 409 Banner
        if (saveStatus is SaveStatus.Conflict) {
            Surface(
                color = PlayfulColors.WarningContainer,
                shape = shapes.cardSmall,
                border = BorderStroke(1.dp, PlayfulColors.WarningBorder),
                modifier = Modifier.fillMaxWidth().padding(horizontal = 16.dp, vertical = 6.dp)
            ) {
                Column(modifier = Modifier.padding(14.dp)) {
                    Row(verticalAlignment = Alignment.CenterVertically) {
                        Icon(
                            imageVector = AppIcons.Warning,
                            contentDescription = null,
                            tint = PlayfulColors.Warning,
                            modifier = Modifier.size(18.dp)
                        )
                        Spacer(modifier = Modifier.width(8.dp))
                        Text(
                            text = "Konflikt wersji (409)",
                            fontWeight = FontWeight.Bold,
                            color = PlayfulColors.OnWarningContainer,
                            fontSize = 14.sp
                        )
                    }
                    Spacer(modifier = Modifier.height(4.dp))
                    Text(
                        text = saveStatus.message,
                        fontSize = 13.sp,
                        color = PlayfulColors.OnWarningContainer
                    )
                    Spacer(modifier = Modifier.height(10.dp))
                    Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                        Button(
                            onClick = onRetrySave,
                            shape = shapes.buttonSmall,
                            colors = ButtonDefaults.buttonColors(
                                containerColor = PlayfulColors.Coral,
                                contentColor = PlayfulColors.TextOnPrimary
                            )
                        ) {
                            Text("Ponów zapis", fontSize = 12.sp)
                        }
                        OutlinedButton(
                            onClick = onReloadFromServer,
                            shape = shapes.buttonSmall,
                            border = BorderStroke(1.dp, PlayfulColors.Border),
                            colors = ButtonDefaults.outlinedButtonColors(
                                contentColor = PlayfulColors.TextPrimary
                            )
                        ) {
                            Text("Wczytaj wersję z serwera", fontSize = 12.sp)
                        }
                    }
                }
            }
        }

        // General Save Error Banner
        if (saveStatus is SaveStatus.Error) {
            Surface(
                color = PlayfulColors.ErrorContainer,
                shape = shapes.cardSmall,
                border = BorderStroke(1.dp, PlayfulColors.ErrorBorder),
                modifier = Modifier.fillMaxWidth().padding(horizontal = 16.dp, vertical = 6.dp)
            ) {
                Column(modifier = Modifier.padding(14.dp)) {
                    Row(verticalAlignment = Alignment.CenterVertically) {
                        Icon(
                            imageVector = AppIcons.Warning,
                            contentDescription = null,
                            tint = PlayfulColors.Error,
                            modifier = Modifier.size(18.dp)
                        )
                        Spacer(modifier = Modifier.width(8.dp))
                        Text(
                            text = "Błąd zapisu",
                            fontWeight = FontWeight.Bold,
                            color = PlayfulColors.OnErrorContainer,
                            fontSize = 14.sp
                        )
                    }
                    Spacer(modifier = Modifier.height(4.dp))
                    Text(
                        text = saveStatus.message,
                        color = PlayfulColors.OnErrorContainer,
                        fontSize = 13.sp
                    )
                    Spacer(modifier = Modifier.height(10.dp))
                    Button(
                        onClick = onRetrySave,
                        shape = shapes.buttonSmall,
                        colors = ButtonDefaults.buttonColors(
                            containerColor = PlayfulColors.Coral,
                            contentColor = PlayfulColors.TextOnPrimary
                        )
                    ) {
                        Text("Ponów zapis", fontSize = 12.sp)
                    }
                }
            }
        }

        // Validation Error Banner
        if (validationError != null) {
            Surface(
                color = PlayfulColors.ErrorContainer,
                shape = shapes.badge,
                border = BorderStroke(1.dp, PlayfulColors.ErrorBorder),
                modifier = Modifier.fillMaxWidth().padding(horizontal = 16.dp, vertical = 4.dp)
            ) {
                Row(
                    modifier = Modifier.padding(horizontal = 12.dp, vertical = 8.dp),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Icon(
                        imageVector = AppIcons.Warning,
                        contentDescription = null,
                        tint = PlayfulColors.Error,
                        modifier = Modifier.size(16.dp)
                    )
                    Spacer(modifier = Modifier.width(8.dp))
                    Text(
                        text = validationError,
                        color = PlayfulColors.OnErrorContainer,
                        fontSize = 12.sp,
                        fontWeight = FontWeight.Medium
                    )
                }
            }
        }

        // General Error Banner
        if (generalError != null) {
            Surface(
                color = PlayfulColors.ErrorContainer,
                shape = shapes.badge,
                border = BorderStroke(1.dp, PlayfulColors.ErrorBorder),
                modifier = Modifier.fillMaxWidth().padding(horizontal = 16.dp, vertical = 4.dp)
            ) {
                Row(
                    modifier = Modifier.padding(horizontal = 12.dp, vertical = 8.dp),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Icon(
                        imageVector = AppIcons.Warning,
                        contentDescription = null,
                        tint = PlayfulColors.Error,
                        modifier = Modifier.size(16.dp)
                    )
                    Spacer(modifier = Modifier.width(8.dp))
                    Text(
                        text = generalError,
                        color = PlayfulColors.OnErrorContainer,
                        fontSize = 12.sp,
                        fontWeight = FontWeight.Medium
                    )
                }
            }
        }
    }
}
